import React from 'react';
import { useFieldArray } from 'react-hook-form';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui';
import { type ChoiceOption, Form, NumberFormField, SelectFormField, TextFormField } from '../../components/form';
import { useBuilderForm } from './useBuilderForm.js';
import {
  type SafeOutputsMap,
  type SafeOutputsValues,
  safeOutputsSchema,
  toEntries,
  toMap,
} from './safeOutputsEntries.js';

export interface SafeOutputsEditorProps {
  value?: SafeOutputsMap | null;
  onChange: (value: SafeOutputsMap) => void;
}

const APPLIES_TO_OPTIONS: ChoiceOption[] = [
  { value: 'all', label: 'All models' },
  { value: 'cloud', label: 'Cloud only' },
  { value: 'local', label: 'Local only' },
];

/**
 * Editor for a workflow step's safe-outputs constraints: a map of tool name to
 * per-tool caps enforced by the engine guard at the tool-call site. Seeded from
 * `value` on mount; the parent should pass key={step.id} so switching steps
 * reseeds. Empty tool names are dropped on serialize.
 */
export const SafeOutputsEditor: React.FC<SafeOutputsEditorProps> = ({ value, onChange }) => {
  const form = useBuilderForm(
    safeOutputsSchema,
    { entries: toEntries(value) },
    (_name, all) => onChange(toMap(all.entries ?? [])),
    { sync: false },
  );
  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'entries' });

  const emit = () => onChange(toMap(form.getValues().entries ?? []));

  return (
    <Form form={form} onSubmit={emit} className="space-y-2 gap-0">
      {fields.length === 0 ? (
        <p className="text-xs text-muted">
          No safe-output constraints. Add one to cap a tool's calls, order value, or inputs.
        </p>
      ) : (
        fields.map((field, i) => (
          <div key={field.id} className="p-2 rounded-sm bg-surface-alt border space-y-3">
            <div className="flex items-end gap-2">
              <TextFormField<SafeOutputsValues>
                name={`entries.${i}.tool`}
                label={`Constraint ${i + 1}: tool name`}
                placeholder="tool-name (e.g. submit-order)"
              />
              <SelectFormField<SafeOutputsValues>
                name={`entries.${i}.appliesTo`}
                label="Applies to"
                options={APPLIES_TO_OPTIONS}
                placeholder="All models"
                className="w-44 shrink-0"
              />
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                onClick={() => {
                  remove(i);
                  emit();
                }}
                className="shrink-0"
                aria-label={`Remove constraint ${i + 1}`}
              >
                <Trash2 size={14} />
              </Button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <NumberFormField<SafeOutputsValues> name={`entries.${i}.maxCalls`} label="Max calls" minValue={0} />
              <NumberFormField<SafeOutputsValues> name={`entries.${i}.maxValue`} label="Max value" minValue={0} />
              <NumberFormField<SafeOutputsValues>
                name={`entries.${i}.maxLength`}
                label="Max input length"
                minValue={0}
              />
              <TextFormField<SafeOutputsValues>
                name={`entries.${i}.allowedValues`}
                label="Allowed values"
                placeholder="BTC, ETH"
                emptyAs=""
              />
            </div>
          </div>
        ))
      )}
      <Button
        variant="secondary"
        size="sm"
        onClick={() => {
          append({ tool: '', appliesTo: 'all' });
          emit();
        }}
      >
        <Plus size={13} /> Add constraint
      </Button>
    </Form>
  );
};
